import { useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, AlertCircle, Edit2, Trash2, Eye, ArrowLeft, Home } from "lucide-react";
import { useLocation } from "wouter";
import { toast } from "sonner";
import { getLoginUrl } from "@/const";

export default function MyApartments() {
  const [, navigate] = useLocation();
  const { user, isAuthenticated, loading } = useAuth();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<any>(null);

  const { data: listings, isLoading, refetch } = trpc.apartments.myListings.useQuery(
    {},
    { enabled: isAuthenticated }
  );
  const getForEditMutation = trpc.apartments.getForEdit.useQuery(
    { id: editingId || 0 },
    { enabled: editingId !== null }
  );
  const updateMutation = trpc.apartments.update.useMutation();
  const deleteMutation = trpc.apartments.delete.useMutation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin w-8 h-8 text-blue-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-12">
        <div className="container mx-auto px-4">
          <Card className="max-w-md mx-auto p-8 text-center">
            <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Login Required</h2>
            <p className="text-slate-600 mb-6">You need to be logged in to view your listings.</p>
            <Button onClick={() => (window.location.href = getLoginUrl("/my-apartments"))} className="w-full bg-blue-600 hover:bg-blue-700">
              Login
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  const handleEdit = async (id: number) => {
    setEditingId(id);
    const listing = await getForEditMutation.refetch();
    if (listing.data) {
      setEditData(listing.data);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingId || !editData) return;

    try {
      await updateMutation.mutateAsync({
        id: editingId,
        ...editData,
      });
      toast.success("Listing updated successfully");
      setEditingId(null);
      setEditData(null);
      refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update listing");
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this listing?")) return;

    try {
      await deleteMutation.mutateAsync({ id });
      toast.success("Listing deleted successfully");
      refetch();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to delete listing");
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">Pending Approval</span>;
      case "approved":
        return <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">Approved</span>;
      case "rejected":
        return <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">Rejected</span>;
      case "expired":
        return <span className="bg-slate-100 text-slate-800 px-3 py-1 rounded-full text-sm font-medium">Expired</span>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation Bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.history.back()}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900"
          >
            <Home className="w-4 h-4" />
            Home
          </Button>
        </div>
      </div>

      <div className="py-12">
      <div className="container mx-auto px-4">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">My Apartment Listings</h1>
          <p className="text-lg text-slate-600">Manage your apartment listings</p>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="animate-spin w-8 h-8 text-blue-600" />
          </div>
        ) : listings?.listings && listings.listings.length > 0 ? (
          <div className="space-y-4">
            {listings.listings.map((listing) => (
              <Card key={listing.id} className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-900">Flat {listing.flatNumber}</h3>
                    <p className="text-slate-600">
                      {listing.bhk} BHK {listing.apartmentType} • {listing.areaInSqFt} sq ft
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(listing.status)}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-slate-600">Type</p>
                    <p className="font-semibold text-slate-900 capitalize">{listing.listingType}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Price</p>
                    <p className="font-semibold text-slate-900">₹{listing.price.toLocaleString("en-IN")}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Posted</p>
                    <p className="font-semibold text-slate-900">{new Date(listing.createdAt).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Expires</p>
                    <p className="font-semibold text-slate-900">{new Date(listing.expiryDate).toLocaleDateString()}</p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        onClick={() => handleEdit(listing.id)}
                        variant="outline"
                        className="flex items-center gap-2"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle>Edit Listing</DialogTitle>
                      </DialogHeader>
                      {editData && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Price (₹)</label>
                            <Input
                              type="number"
                              value={editData.price || ""}
                              onChange={(e) => setEditData({ ...editData, price: parseInt(e.target.value) })}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Area (sq ft)</label>
                            <Input
                              type="number"
                              value={editData.areaInSqFt || ""}
                              onChange={(e) => setEditData({ ...editData, areaInSqFt: parseInt(e.target.value) })}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                            <Textarea
                              value={editData.description || ""}
                              onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                              rows={4}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                            <Input
                              type="tel"
                              value={editData.residentPhone || ""}
                              onChange={(e) => setEditData({ ...editData, residentPhone: e.target.value })}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                            <Input
                              type="email"
                              value={editData.residentEmail || ""}
                              onChange={(e) => setEditData({ ...editData, residentEmail: e.target.value })}
                            />
                          </div>

                          <Button
                            onClick={handleSaveEdit}
                            disabled={updateMutation.isPending}
                            className="w-full bg-blue-600 hover:bg-blue-700"
                          >
                            {updateMutation.isPending ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Saving...
                              </>
                            ) : (
                              "Save Changes"
                            )}
                          </Button>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>

                  <Button
                    onClick={() => handleDelete(listing.id)}
                    disabled={deleteMutation.isPending}
                    variant="destructive"
                    className="flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-8 text-center">
            <Eye className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">No Listings Yet</h3>
            <p className="text-slate-600 mb-6">You haven't posted any apartment listings yet.</p>
            <Button onClick={() => (window.location.href = "/post-apartment")} className="bg-blue-600 hover:bg-blue-700">
              Post Your First Apartment
            </Button>
          </Card>
        )}
      </div>
      </div>
    </div>
  );
}
